import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Wallet, Warning, CaretRight, Path } from "@phosphor-icons/react";

/**
 * Dashboard block — daily paniers + zone de trajet, visible for admin/compta/associate.
 * Renders nothing for other roles, so it can safely be added to the block list.
 */
export default function PayrollCard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  const canSee = ["admin", "compta", "associate", "manager"].includes(user?.role);
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    if (!canSee) return;
    api.get(`/payroll/daily?date=${today}`)
      .then((r) => setRows(r.data?.rows || []))
      .catch((e) => setError(e?.response?.data?.detail || "Erreur chargement"));
  }, [canSee, today]);

  if (!canSee) return null;

  const totalPaniers = (rows || []).reduce((n, r) => n + (r.panier || 0), 0);
  const missing = (rows || []).filter((r) => (r.missing_address || []).length > 0);

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden" data-testid="payroll-card">
      <button
        onClick={() => nav("/heures")}
        className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-white/[0.04] transition-colors"
        data-testid="payroll-card-header"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#22C55E]/15 border border-[#22C55E]/30 flex items-center justify-center">
            <Wallet size={19} weight="duotone" className="text-[#4ADE80]" />
          </div>
          <div className="text-left">
            <div className="text-[10px] uppercase tracking-widest text-white/50">Paniers &amp; Trajets · aujourd&apos;hui</div>
            <div className="font-display font-semibold">{totalPaniers} panier(s) · {rows?.length || 0} tech</div>
          </div>
        </div>
        <CaretRight size={16} className="text-white/40" />
      </button>

      {error && (
        <div className="px-4 pb-3 text-xs text-red-400">{error}</div>
      )}

      {!error && rows === null && (
        <div className="px-4 pb-4 space-y-2">
          {[0, 1].map((i) => <div key={i} className="h-9 rounded-lg bg-white/5 animate-pulse" />)}
        </div>
      )}

      {rows && rows.length === 0 && !error && (
        <div className="px-4 pb-4 text-xs text-white/50">Aucune intervention terrain aujourd&apos;hui.</div>
      )}

      {rows && rows.length > 0 && (
        <div className="px-4 pb-4 space-y-1.5">
          {rows.slice(0, 6).map((r) => (
            <button
              key={r.user_id}
              onClick={() => nav("/heures")}
              className="w-full flex items-center justify-between rounded-lg bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 px-3 py-2 text-sm"
              data-testid={`payroll-card-row-${r.user_id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="truncate">{r.user_name || "—"}</span>
                {r.overridden && <span className="text-[9px] uppercase tracking-widest text-amber-400 px-1.5 py-0.5 rounded bg-amber-500/10">ajusté</span>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.farthest_km !== null && r.farthest_km !== undefined && (
                  <span className="text-[10px] text-white/50 tabular-nums flex items-center gap-1"><Path size={11} /> {r.farthest_km} km</span>
                )}
                <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-md bg-[#005CFF]/20 text-[#7CB2FF]">
                  {r.zone || "—"}
                </span>
                <span className="text-[10px] uppercase tracking-widest text-white/50">P {r.panier || 0}</span>
              </div>
            </button>
          ))}
          {rows.length > 6 && (
            <div className="text-[10px] text-white/40 text-center pt-1">… et {rows.length - 6} autre(s) — voir le rapport</div>
          )}
          {missing.length > 0 && (
            <div className="mt-2 rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-xs text-amber-200 flex items-start gap-2" data-testid="payroll-card-missing">
              <Warning size={14} className="shrink-0 mt-0.5" />
              <span>
                {missing.length} tech avec adresse chantier incorrecte ou manquante — zone non calculée. Vérifie les chantiers signalés dans le rapport.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
